
import { defineComponent, ref, computed, PropType, Ref, watch } from "vue";
import {
  useGetProfilesByLevelOfUseQuery,
  ProfileType,
  LevelOfUse,
  GetProfilesByLevelOfUseQuery,
  RoleAssigneeType,
} from "@/graphql/types";
import { useResult } from "@vue/apollo-composable";
import VDPopupListbox from "@/components/pickers/VDPopupListbox.vue";
import { VDPopupListboxClass } from "@/components/pickers/VDPopupListbox";
import GroupedItems from "@/models/GroupedItems";

export default defineComponent({
  name: "UserRoleProfilePicker",
  components: {
    VDPopupListbox,
  },
  emits: ["profilesSelected"],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    addedProfiles: {
      type: Array as PropType<ProfileType[]>,
      default: () => [],
    },
    assigneeRoles: {
      type: Array as PropType<RoleAssigneeType[]>,
      required: true,
    },
  },
  setup(props, { emit }) {
    // VDOM
    const listbox = ref<VDPopupListboxClass>();

    // Refs
    const isEnabled = ref(false);

    // Data - Query for user-level profiles
    const getProfilesResponse = useGetProfilesByLevelOfUseQuery(
      () => ({ levelOfUse: LevelOfUse.User }),
      () => ({
        enabled: isEnabled.value,
        fetchPolicy: "cache-first",
        errorPolicy: "all",
      })
    );

    // Computed
    const loading = computed(() => {
      return getProfilesResponse.loading.value;
    });

    const error = computed(() => {
      return getProfilesResponse.error.value;
    });

    // Get organization's assigned on-behalf-of roles for filtering
    const organizationOnBehalfRoles = computed(() => {
      return (props.assigneeRoles || []).filter((role) => role?.isOnBehalf);
    });

    function onProfileSelected(profile: any) {
      // Filter roles to only include those assigned to the organization as on-behalf-of
      const availableRoles: any[] = [];
      const unavailableRoles: any[] = [];

      if (profile.roles && profile.roles.length > 0) {
        profile.roles.forEach((role: any) => {
          const isAssignedToOrgAsOnBehalf =
            organizationOnBehalfRoles.value.some(
              (orgRole: any) => orgRole?.roleId === role.roleId
            );

          if (isAssignedToOrgAsOnBehalf) {
            availableRoles.push(role);
          } else {
            unavailableRoles.push(role);
          }
        });
      }

      // Emit event with filtered roles and information about unavailable roles
      emit("profilesSelected", {
        profile,
        availableRoles,
        unavailableRoles,
      });
    }

    function getAssignableRolesCount(profile: any): number {
      if (!profile.roles || profile.roles.length === 0) {
        return 0;
      }

      return profile.roles.filter((role: any) => {
        return organizationOnBehalfRoles.value.some(
          (orgRole: any) => orgRole?.roleId === role.roleId
        );
      }).length;
    }

    return {
      groupedProfiles: useGroupedProfilesByApplication(
        getProfilesResponse.result
      ),
      listbox,
      loading,
      error,
      onProfileSelected,
      getAssignableRolesCount,
      toggle: (event: Event) => {
        isEnabled.value = true;
        listbox.value?.toggle(event);
      },
    };
  },
});

function useGroupedProfilesByApplication(
  profilesQuery: Ref<GetProfilesByLevelOfUseQuery | undefined>
) {
  const groupedProfiles = ref<GroupedItems<ProfileType>[]>([]);

  watch(
    () => profilesQuery.value,
    (newValue) => {
      const map: { [name: string]: ProfileType[] } = {};

      newValue?.profilesByLevelOfUse?.forEach((element: any) => {
        if (element?.application?.name && map[element.application.name]) {
          map[element.application.name].push(element);
        } else if (element?.application?.name) {
          map[element.application.name] = [element];
        }
      });

      groupedProfiles.value = Object.keys(map)
        .sort()
        .map((element) => {
          return {
            name: element,
            items: map[element].sort(
              (a, b) => a.name?.localeCompare(b.name ?? "") ?? -1
            ),
          };
        });
    },
    { deep: true }
  );

  return groupedProfiles;
}

// Export type for parent components
export declare class UserRoleProfilePickerClass {
  toggle(event: Event): void;
  hide(): void;
}
